import React, { useState } from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaUsers, FaChartLine, FaTimes, FaArrowRight } from 'react-icons/fa';

const Services = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [activeModal, setActiveModal] = useState(null);

  const services = [
    {
      id: 'individual',
      title: 'Individual Coaching',
      icon: <FaUser />,
      description: 'One-on-one coaching for personal and professional development, helping you uncover strengths, values, and beliefs.',
      details: 'In these personalised sessions, we engage either virtually or in person, meeting regularly to work toward your specific goals. You set the agenda and take full ownership of your progress, while I support you at every step, helping you discover your hidden potential and authentic self.'
    },
    {
      id: 'team',
      title: 'Team Coaching',
      icon: <FaUsers />,
      description: 'Small group coaching working towards collective aims, strengthening interpersonal dynamics and team collaboration.',
      details: 'Team coaching engages with your group either as a whole or through a mix of group and one-on-one sessions. This approach has proven effective for succession planning, strategic development, and other team initiatives, fostering improved communication and aligned goals.'
    },
    {
      id: 'executive',
      title: 'Executive Leadership',
      icon: <FaChartLine />,
      description: 'Specialised coaching for executives and leaders to enhance leadership abilities and decision-making.',
      details: 'Executive coaching focuses on developing strategic thinking, emotional intelligence, and authentic leadership presence. We work together to refine your leadership style, improve communication with stakeholders, and balance professional demands with personal wellbeing.'
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: 0.1 * i,
        duration: 0.5
      }
    })
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.3,
      }
    }
  };

  return (
    <ServicesContainer id="services">
      <div className="container">
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
        >
          Coaching Services
        </SectionTitle>
        
        <ServicesGrid ref={ref}>
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
            >
              <ServiceIconWrapper>
                <ServiceIcon>{service.icon}</ServiceIcon>
              </ServiceIconWrapper>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <LearnMore onClick={() => setActiveModal(service.id)}>
                Learn More <FaArrowRight className="arrow" />
              </LearnMore>
              <CardDecoration className="decoration-1" />
              <CardDecoration className="decoration-2" />
            </ServiceCard>
          ))}
        </ServicesGrid>
        
        <AnimatePresence>
          {activeModal && (
            <ModalOverlay 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveModal(null)}
            >
              <ModalContent
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={(e) => e.stopPropagation()}
              >
                <CloseButton onClick={() => setActiveModal(null)}>
                  <FaTimes />
                </CloseButton>
                
                <ModalBody>
                  <ModalIconWrapper>
                    <ModalIcon>
                      {services.find(s => s.id === activeModal).icon}
                    </ModalIcon>
                  </ModalIconWrapper>
                  
                  <ModalTitle>{services.find(s => s.id === activeModal).title}</ModalTitle>
                  
                  <ModalDescription>
                    <p><Highlight>{services.find(s => s.id === activeModal).description}</Highlight></p>
                    <p>{services.find(s => s.id === activeModal).details}</p>
                  </ModalDescription>
                  
                  <ModalButton onClick={() => {
                    setActiveModal(null);
                    document.getElementById('booking').scrollIntoView({ behavior: 'smooth' });
                  }}>
                    Book a Session
                  </ModalButton>
                </ModalBody>
                
                <ModalDecoration className="decoration-1" />
                <ModalDecoration className="decoration-2" />
              </ModalContent>
            </ModalOverlay>
          )}
        </AnimatePresence>
      </div>
      <BackgroundElement className="element-1" />
      <BackgroundElement className="element-2" />
      <DiagonalDivider />
    </ServicesContainer>
  );
};

const ServicesContainer = styled.section`
  background-color: #f9f9f9;
  padding: 100px 0 120px;
  position: relative;
  overflow: hidden;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 70px;
  position: relative;
  color: #1A2A3A;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: #A8C4A0;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 40px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const ServiceCard = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  padding: 40px 30px 30px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  
  h3 {
    margin-top: 25px;
    font-size: 1.5rem;
    color: #1A2A3A;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 0;
      width: 40px;
      height: 2px;
      background-color: #A8C4A0;
    }
  }
  
  p {
    color: #555;
    margin-top: 25px;
    flex-grow: 1;
    line-height: 1.6;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
    
    ${props => `
      .decoration-1 {
        transform: translateY(-10px) rotate(-5deg);
      }
      
      .decoration-2 {
        transform: translateY(10px) rotate(5deg);
      }
    `}
  }
`;

const CardDecoration = styled.div`
  position: absolute;
  background-color: rgba(90, 127, 140, 0.04);
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  z-index: -1;
  transition: all 0.5s ease;
  
  &.decoration-1 {
    width: 200px;
    height: 200px;
    top: -40px;
    right: -40px;
  }
  
  &.decoration-2 {
    width: 120px;
    height: 120px;
    bottom: -30px;
    left: -30px;
    background-color: rgba(168, 196, 160, 0.04);
  }
`;

const ServiceIconWrapper = styled.div`
  display: inline-block;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    width: calc(100% + 10px);
    height: calc(100% + 10px);
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(168, 196, 160, 0.2) 0%, rgba(90, 127, 140, 0.2) 100%);
    opacity: 0.6;
    z-index: -1;
  }
`;

const ServiceIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: #5A7F8C;
  box-shadow: 0 8px 20px rgba(90, 127, 140, 0.15);
`;

const LearnMore = styled.button`
  background: none;
  border: none;
  color: #5A7F8C;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 0;
  margin-top: 20px;
  position: relative;
  align-self: flex-start;
  display: flex;
  align-items: center;
  
  .arrow {
    font-size: 0.8rem;
    margin-left: 8px;
    transition: transform 0.3s ease;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: #5A7F8C;
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
  
  &:hover .arrow {
    transform: translateX(5px);
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled(motion.div)`
  background-color: white;
  border-radius: 15px;
  width: 100%;
  max-width: 650px;
  position: relative;
  overflow: hidden;
  max-height: 90vh;
`;

const ModalBody = styled.div`
  padding: 50px 40px 40px;
  position: relative;
  z-index: 2;
  overflow-y: auto;
  max-height: 80vh;
`;

const ModalIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 25px;
`;

const ModalIcon = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(168, 196, 160, 0.2) 0%, rgba(90, 127, 140, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: #5A7F8C;
  box-shadow: 0 10px 25px rgba(90, 127, 140, 0.2);
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    border-radius: 50%;
    border: 1px solid rgba(168, 196, 160, 0.3);
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.5;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const ModalTitle = styled.h3`
  font-size: 2rem;
  color: #1A2A3A;
  text-align: center;
  margin-bottom: 30px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background-color: #A8C4A0;
  }
`;

const ModalDescription = styled.div`
  p {
    margin-bottom: 20px;
    line-height: 1.7;
    color: #555;
    font-size: 1.05rem;
    
    &:first-child {
      font-size: 1.15rem;
      color: #1A2A3A;
    }
  }
`;

const Highlight = styled.span`
  position: relative;
  color: #5A7F8C;
  font-weight: 500;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 2px;
    left: 0;
    width: 100%;
    height: 5px;
    background-color: rgba(168, 196, 160, 0.3);
    z-index: -1;
  }
`;

const ModalButton = styled.button`
  display: block;
  width: 100%;
  padding: 15px 25px;
  margin-top: 30px;
  background-color: #A8C4A0;
  color: white;
  text-align: center;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.05rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(168, 196, 160, 0.3);
  
  &:hover {
    background-color: #97b38f;
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(168, 196, 160, 0.4);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 1.2rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  background-color: rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    color: #1A2A3A;
    transform: rotate(90deg);
  }
`;

const ModalDecoration = styled.div`
  position: absolute;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  z-index: 1;
  
  &.decoration-1 {
    width: 300px;
    height: 300px;
    top: -150px;
    right: -150px;
    background-color: rgba(168, 196, 160, 0.03);
  }
  
  &.decoration-2 {
    width: 250px;
    height: 250px;
    bottom: -100px;
    left: -100px;
    background-color: rgba(90, 127, 140, 0.03);
  }
`;

const BackgroundElement = styled.div`
  position: absolute;
  
  &.element-1 {
    width: 600px;
    height: 600px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v50H50v50H0z' fill='%23A8C4A0' fill-opacity='0.05'/%3E%3C/svg%3E");
    top: -200px;
    left: -200px;
    z-index: 0;
    transform: rotate(15deg);
  }
  
  &.element-2 {
    width: 500px;
    height: 500px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h50v50H0z' fill='%235A7F8C' fill-opacity='0.03'/%3E%3C/svg%3E");
    bottom: -100px;
    right: -150px;
    z-index: 0;
    transform: rotate(-10deg);
  }
`;

const DiagonalDivider = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background-color: white;
  clip-path: polygon(0 100%, 100% 40%, 100% 100%, 0 100%);
  z-index: 1;
`;

export default Services;